/**
 * 100-Day IP Race 시즌 설정 + 진행 계산.
 * 시작/종료일은 추후 기획 확정 — 아래 PLACEHOLDER 날짜만 바꾸면 된다 (백엔드
 * get_home_summary.season 연동 시 이 모듈이 서버 값을 우선하도록 교체 예정).
 */

export const SEASON_TOTAL_DAYS = 100;

export type SeasonConfig = {
  /** ISO 문자열. null = 시즌 미정 (coming soon 상태 렌더) */
  startsAt: string | null;
  endsAt: string | null;
  label: string;
};

// PLACEHOLDER — 기획 확정 전 임시 날짜 (UI 검수용). 확정 시 이 두 값만 교체.
export const SEASON_CONFIG: SeasonConfig = {
  startsAt: "2026-07-01T00:00:00Z",
  endsAt: "2026-10-09T00:00:00Z", // startsAt + 100일
  label: "SEASON 1",
};

/**
 * R-17 시즌 상태 머신 — 준비/진행/스냅샷/정산 4상태 (§6.1).
 * 스냅샷 창: 종료 시각 직전 24h를 "snapshot 임박"으로, 종료 후 14일을 정산 기간으로
 * 간주한다(플레이스홀더 — 서버 시즌 API 연동 시 서버 상태를 우선).
 */
export type SeasonPhase = "prep" | "live" | "snapshot" | "settlement";

export const SNAPSHOT_WINDOW_MS = 24 * 3_600_000;
export const SETTLEMENT_WINDOW_MS = 14 * 86_400_000;

export function seasonPhase(config: SeasonConfig = SEASON_CONFIG, now: Date = new Date()): SeasonPhase {
  if (!config.startsAt || !config.endsAt) return "prep";
  const t = now.getTime();
  const start = Date.parse(config.startsAt);
  const end = Date.parse(config.endsAt);
  if (t < start) return "prep";
  if (t < end - SNAPSHOT_WINDOW_MS) return "live";
  if (t < end) return "snapshot";
  if (t < end + SETTLEMENT_WINDOW_MS) return "settlement";
  return "prep"; // 정산 종료 후 다음 시즌 준비 상태로 회귀
}

export type SeasonProgress = {
  /** 0..1 경과 비율 (시작 전 0, 종료 후 1) */
  ratio: number;
  /** 1-based 현재 일차 (시작 전 0, 종료 후 total) */
  day: number;
  totalDays: number;
  /** "87D 12H" 형태의 남은 시간 (분 단위 절사). 종료 후 null */
  remainingLabel: string | null;
  phase: "upcoming" | "live" | "ended";
};

export function seasonProgress(config: SeasonConfig, now: Date = new Date()): SeasonProgress | null {
  if (!config.startsAt || !config.endsAt) return null;
  const start = Date.parse(config.startsAt);
  const end = Date.parse(config.endsAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;

  const t = now.getTime();
  const span = end - start;
  const ratio = Math.min(1, Math.max(0, (t - start) / span));
  const day = Math.min(SEASON_TOTAL_DAYS, Math.max(0, Math.floor((t - start) / 86_400_000) + 1));

  let phase: SeasonProgress["phase"] = "live";
  if (t < start) phase = "upcoming";
  else if (t >= end) phase = "ended";

  let remainingLabel: string | null = null;
  if (phase !== "ended") {
    const target = phase === "upcoming" ? start : end;
    const ms = target - t;
    const days = Math.floor(ms / 86_400_000);
    const hours = Math.floor((ms % 86_400_000) / 3_600_000);
    const mins = Math.floor((ms % 3_600_000) / 60_000);
    remainingLabel = days > 0 ? `${days}D ${hours}H` : hours > 0 ? `${hours}H ${mins}M` : `${mins}M`;
  }

  return { ratio, day, totalDays: SEASON_TOTAL_DAYS, remainingLabel, phase };
}
